import React from "react";
import { Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import stackFillIcon from "../../../../assets/images/product/specmanager/stack-fill.svg";
import microscopeIcon from "../../../../assets/images/product/specmanager/microscope-line.svg";
import shieldCheckIcon from "../../../../assets/images/product/specmanager/shield-check-line.svg";
import informationIcon from "../../../../assets/images/product/specmanager/information-line.svg";
import arrowRightLineIcon from "../../../../assets/images/product/specmanager/arrow-right-line.svg";
import {
  formatSpecValueFromEditorShape,
  formatCustomSpecsFromApi,
  formatMrlCountriesListSubtitleFromEditorShape,
  isCustomSpecProduct,
  getTestTypeDisplayLabel,
  formatProductTitle,
  formatLocationTitle,
  getTestSpecEntityLabels,
  SPEC_TYPE,
  canEditSpecs,
} from "../utils/utils";
import { CoverageSummarySection, SpecStatusChip } from "../shared/specManagerUi";
import "./TestSpecOverview.css";

export default function TestSpecOverview({
  testName,
  specRows,
  products,
  identifierFields,
  testSpecStatus,
  loadFailed,
  onEditClick,
  onBack,
  specType = SPEC_TYPE.PRODUCT,
}) {
  const specRow = specRows.length > 0 ? specRows[0] : null;
  const hasSpecData = Boolean(specRow);
  const countriesList = specRow && specRow.countries_lst != null ? specRow.countries_lst : null;
  const hasMrl = Array.isArray(countriesList) && countriesList.length > 0;
  const canEdit = canEditSpecs() && !loadFailed;
  const entityLabels = getTestSpecEntityLabels(specType);
  const isEnvironmentSpec = specType === SPEC_TYPE.ENVIRONMENT;

  const standardProductCount = products.filter((productRow) => !isCustomSpecProduct(productRow)).length;

  const formatAttachedItemTitle = (itemRow) => {
    if (isEnvironmentSpec) {
      return formatLocationTitle(itemRow, identifierFields);
    }
    return formatProductTitle(itemRow, identifierFields);
  };

  let testTypeDisplay = "Not defined";
  if (!loadFailed && hasSpecData) {
    if (hasMrl) {
      testTypeDisplay = "MRL";
    } else {
      testTypeDisplay = getTestTypeDisplayLabel(specRow.test_type) || "Not defined";
    }
  }

  let specValueDisplay = "Not defined";
  if (!loadFailed && hasSpecData) {
    if (hasMrl) {
      specValueDisplay = formatMrlCountriesListSubtitleFromEditorShape(countriesList, {
        includeMrlPrefix: false,
      });
    } else {
      specValueDisplay = formatSpecValueFromEditorShape(specRow);
    }
  }

  const isTestTypeMissing = testTypeDisplay === "Not defined";
  const isSpecValueMissing = !specValueDisplay || specValueDisplay === "Not defined";

  return (
    <div className="SpecManager_EditorRoot TestSpecOverview_Root">
      <div className="TestSpecOverview_Header">
        <div className="TestSpecOverview_HeaderRow">
          <div className="TestSpecOverview_HeaderLeft">
            <button
              type="button"
              className="TestSpecOverview_BackButton"
              onClick={onBack}
              aria-label="Back to test list"
            >
              <ArrowLeftOutlined />
            </button>
            <div className="TestSpecOverview_TestName" title={testName}>
              {testName}
            </div>
          </div>
          <div className="TestSpecOverview_HeaderRight">
            <div className="TestSpecOverview_ProductCountBadge">
              <img
                src={stackFillIcon}
                alt=""
                className="TestSpecOverview_ProductCountBadgeIcon"
                aria-hidden
              />
              <span>{products.length}</span>
            </div>
            <SpecStatusChip status={testSpecStatus} variant="overview" />
          </div>
        </div>
      </div>

      <div className="SpecManager_EditorBody">
        <div className="TestSpecOverview_BodyScroll">
          <div className="TestSpecOverview_DetailsArea">
            <div className="TestSpecOverview_DetailsBox">
              <div className="TestSpecOverview_DetailsColumns">
                <div className="TestSpecOverview_StandardSection">
                  <div className="TestSpecOverview_SectionTitleBar">
                    <div className="TestSpecOverview_SectionTitle">Test Standard</div>
                  </div>
                  <div className="TestSpecOverview_StandardRows">
                    <div className="TestSpecOverview_StandardRow">
                      <div className="TestSpecOverview_RowLabel">Test Type</div>
                      <div className={`TestSpecOverview_RowValue${isTestTypeMissing ? " TestSpecOverview_RowValue--notDefined" : ""}`}>
                        {testTypeDisplay}
                      </div>
                    </div>
                    <div className="TestSpecOverview_StandardRow">
                      <div className="TestSpecOverview_RowLabel">Spec</div>
                      <div className={`TestSpecOverview_RowValue${isSpecValueMissing ? " TestSpecOverview_RowValue--notDefined" : ""}`}>
                        {hasMrl ? specValueDisplay || "MRL" : specValueDisplay}
                      </div>
                    </div>
                  </div>
                </div>
                <CoverageSummarySection products={products} specType={specType} />
              </div>
            </div>
          </div>

          <div className="TestSpecOverview_ProductsArea">
            <div className="TestSpecOverview_AttachedProductsTitle">{entityLabels.attachedTitle}</div>
            {products.length === 0 && (
              <div className="TestSpecOverview_EmptyState">
                {canEdit && entityLabels.emptyCanEdit}
                {!canEdit && entityLabels.emptyViewOnly}
              </div>
            )}

            {products.length > 0 && (
              <ul className="TestSpecOverview_AttachedList">
                {products.map((productRow) => {
                  const isCustom = entityLabels.showCustomBadges && isCustomSpecProduct(productRow);
                  const customSpecsLabel = formatCustomSpecsFromApi(productRow.custom_specs);
                  return (
                    <li key={productRow.product_id} className="TestSpecOverview_AttachedProductItem">
                      <span className="TestSpecOverview_AttachedProductText">
                        {formatAttachedItemTitle(productRow)}
                      </span>
                      {isCustom && (
                        <span className="TestSpecOverview_ProductBadge TestSpecOverview_ProductBadge--custom">
                          <img
                            src={informationIcon}
                            alt=""
                            className="TestSpecOverview_BadgeIcon"
                            aria-hidden
                          />
                          <span>
                            Custom
                            {customSpecsLabel && `: ${customSpecsLabel}`}
                          </span>
                        </span>
                      )}

                      {!isCustom && (
                        <span className="TestSpecOverview_ProductBadge TestSpecOverview_ProductBadge--standard">
                          <img
                            src={shieldCheckIcon}
                            alt=""
                            className="TestSpecOverview_BadgeIcon"
                            aria-hidden
                          />
                          <span>Standard</span>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="TestSpecOverview_Footer">
        <div className="TestSpecOverview_FooterFollowsBadge">
          <img
            src={microscopeIcon}
            alt=""
            className="TestSpecOverview_FooterBadgeIcon"
            aria-hidden
          />
          <span>
            <strong>
              {standardProductCount}
              {" "}
              {entityLabels.plural}
            </strong>
            {" "}
            follow the standard spec
          </span>
        </div>
        {canEdit && (
          <Button type="primary" className="TestSpecOverview_EditButton" onClick={onEditClick}>
            <span>Edit Spec</span>
            <img src={arrowRightLineIcon} alt="" className="TestSpecOverview_EditButtonIcon" aria-hidden />
          </Button>
        )}
      </div>
    </div>
  );
}
